import React, {useRef} from "react"
import {
	useAccessToken,
	useIsManagedSolutionsBankingUser,
	useIsUnitAdminUser,
	useIsUnitReadonlyUser,
	useLogout,
	useUserClaimsData,
} from "../../services/auth"
import {useToggle, useClickAway} from "react-use"
import {HasPermission} from "../../containers/PermissionedUser/Permission"
import Icon from "../Icon/Icon"
import classNames from "classnames"
import {NavLink} from "../NavLink/NavLink"
import ShareBoxInterfaceEssentialIcon from "./ShareBoxInterfaceEssentialIcon"
import {useModal} from "react-modal-hook"
import {SwitchBuildPathModeModal} from "../SwitchBuildPathModeModal/SwitchBuildPathModeModal"
import {useOrgBankingSetup} from "../../services/orgBankingSetup"
import {BuildPathType, isBuildPathType} from "../../pages/ChooseBuildPath/buildPathOptions"
import {updateOrgBankingSetup} from "../../resources/orgBankingSetupType"
import {useToasts} from "react-toast-notifications"
import {WebauthnRegisterModal, WebauthnRegisterFinishResponse} from "../Webauthn/WebauthnRegisterModal"
import {isLocalEnv, isSandboxEnv} from "../../utilities/environment"

interface NavbarUserDropdownProps {
	narrow?: boolean
}

export default function NavbarUserDropdown({narrow}: NavbarUserDropdownProps) {
	const ref = useRef(null)
	const [isDropdownActive, toggleDropdown] = useToggle(false)
	useClickAway(ref, () => toggleDropdown(false))
	const dropdownClassname = classNames("navbar-user-dropdown dropdown is-right", isDropdownActive && "is-active")
	const userTokenData = useUserClaimsData()
	const logout = useLogout()
	const {addToast} = useToasts()
	const orgName = userTokenData.org
	const isUnitAdmin = useIsUnitAdminUser()
	const isUnitReadonly = useIsUnitReadonlyUser()
	const isSandbox = isSandboxEnv()
	const isLocal = isLocalEnv()
	const userFirstLetter = userTokenData.sub ? userTokenData.sub[0] : "U"
	const {fetch, setup} = useOrgBankingSetup()
	const accessToken = useAccessToken()
	const isManagedSolutionsBankingUser = useIsManagedSolutionsBankingUser()

	const [showWebauthnRegisterModal, hideWebauthnRegisterModal] = useModal(() => {
		const WebauthnRegisterFinished = (e: any) => {
			const event = e as WebauthnRegisterFinishResponse
			if ("error" in event.detail) {
				const errorTitle = event.detail.error.errors[0].title
				if (errorTitle.includes("credentials already registered with the relying party")) {
					addToast("User is already registered with biometric authentication on this device", {appearance: "warning"})
					hideWebauthnRegisterModal()
				} else {
					addToast(`Biometric registration failed - ${errorTitle}`, {appearance: "error"})
				}
			} else {
				const isSuccess = event.detail.value.data.attributes.isSuccess
				isSuccess
					? addToast("Biometric registered successfully", {appearance: "success"})
					: addToast("Biometric registration failed", {appearance: "error"})
				hideWebauthnRegisterModal()
			}
		}

		return (
			<WebauthnRegisterModal
				closeModal={hideWebauthnRegisterModal}
				onSuccess={hideWebauthnRegisterModal}
				eventHandler={WebauthnRegisterFinished}
			/>
		)
	}, [])

	const [showSwitchBuildPathModal, hideSwitchBuildPathModal] = useModal(() => {
		if (!isBuildPathType(setup)) {
			return null
		}
		const onSwitch = async (selectedMode: BuildPathType) => {
			await updateOrgBankingSetup(accessToken, selectedMode)
			fetch()
		}
		return <SwitchBuildPathModeModal close={hideSwitchBuildPathModal} currentMode={setup} onSwitch={onSwitch} />
	})

	return (
		<div className={dropdownClassname} ref={ref}>
			<div className="navbar-user-dropdown-trigger" onClick={() => toggleDropdown()}>
				<div className="navbar-user-dropdown-org-info">{orgName ?? "Unit"}</div>
				<div className="navbar-user-dropdown-user-letter">{userFirstLetter}</div>
			</div>
			<div className="dropdown-menu">
				<div className="dropdown-content">
					<div className="navbar-user-dropdown-content-details">
						<div className="navbar-user-dropdown-content-user-info">
							<div className="navbar-user-dropdown-user-letter large">{userFirstLetter}</div>
							<div className="navbar-user-dropdown-content-username">{userTokenData.sub}</div>
						</div>
					</div>
					<hr className="navbar-user-dropdown-content-divider" />
					<ul className="navbar-user-dropdown-content-links" onClick={toggleDropdown}>
						{!narrow && (
							<>
								<HasPermission resource="user" action="get">
									<li>
										<NavLink end className="has-icon" activeClassName="is-active" to="/users">
											<span className="icon">
												<Icon icon="user-group" size={16} />
											</span>
											<span className="menu-item-label">Manage Users</span>
										</NavLink>
									</li>
								</HasPermission>
								{(setup === "ReadyToLaunchBanking" || setup === "Custom") && !isManagedSolutionsBankingUser && (
									<li>
										<NavLink
											to="#"
											end
											className="has-icon"
											onClick={(e) => {
												e.preventDefault()
												toggleDropdown(false)
												showSwitchBuildPathModal()
											}}
										>
											<span className="icon">
												<Icon icon="Data-Transfer-Refresh" size={16} />
											</span>
											<span className="menu-item-label">Switch Build Path</span>
										</NavLink>
									</li>
								)}
								{(isUnitAdmin || isUnitReadonly) && (
									<li>
										<NavLink end className="has-icon" activeClassName="is-active" to="/admin">
											<span className="icon">
												<Icon icon="settings-cog-1" size={16} />
											</span>
											<span className="menu-item-label">Admin</span>
										</NavLink>
									</li>
								)}
								<hr className="navbar-user-dropdown-content-divider" />
								<li>
									<NavLink end className="has-icon" activeClassName="is-active" to="/change-password">
										<span className="icon">
											<Icon icon="security-password-lock--programing-apps-websites" size={16} />
										</span>
										<span className="menu-item-label">Change Password</span>
									</NavLink>
								</li>
								{isLocal || (isSandbox && orgName === "German") ? (
									<li>
										<NavLink
											end
											className="has-icon"
											activeClassName="is-active"
											to="#"
											onClick={showWebauthnRegisterModal}
										>
											<span className="icon">
												<Icon icon="face-id-user--interface-essential" size={16} />
											</span>
											<span className="menu-item-label">Add Biometric Authentication</span>
										</NavLink>
									</li>
								) : null}
							</>
						)}
						<li>
							<NavLink end className="has-icon" activeClassName="is-active" to="#" onClick={logout}>
								<span className="icon">
									<ShareBoxInterfaceEssentialIcon />
								</span>
								<span className="menu-item-label">Logout</span>
							</NavLink>
						</li>
					</ul>
				</div>
			</div>
		</div>
	)
}
